<template>
  <hit-modal
    v-if="open"
    :title="modalTitle"
    prefix-icon="group"
    popup-size="large"
    @cancel="$emit('close')"
  >
    <div v-if="loading" class="p-4 text-slate-500">
      {{ $t('hit-admin.notifications.recipients-loading') }}
    </div>
    <div v-else-if="error" class="p-4 text-red-700">
      {{ error }}
    </div>
    <template v-else>
      <div v-if="!tracked" class="info-banner mb-3">
        {{ $t('hit-admin.notifications.deliveries-not-tracked') }}
      </div>
      <div class="summary mb-3 text-sm text-slate-600">
        {{
          $t('hit-admin.notifications.deliveries-summary', {count: rows.length})
        }}
      </div>
      <div class="table-wrap">
        <table class="deliveries-table">
          <thead>
            <tr>
              <th>{{ $t('hit-admin.notifications.col-name') }}</th>
              <th>{{ $t('hit-admin.notifications.col-email') }}</th>
              <th>{{ $t('hit-admin.notifications.col-company') }}</th>
              <th>
                {{ $t('hit-admin.notifications.col-notification-status') }}
              </th>
              <th>{{ $t('hit-admin.notifications.col-email-status') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in rows"
              :key="`${row.user_id}-${row.company_id}-${idx}`"
            >
              <td>{{ row.name || '—' }}</td>
              <td>{{ row.email || '—' }}</td>
              <td>{{ row.company_designation || '—' }}</td>
              <td>
                <span class="status-pill" :class="row.delivery_status">
                  {{ statusLabel(row.delivery_status) }}
                </span>
              </td>
              <td>
                <span
                  class="status-pill email-status"
                  :class="row.email_status"
                >
                  {{ emailStatusLabel(row.email_status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <div class="flex justify-end mt-4">
      <hit-button
        :label="$t('hit-admin.notifications.close')"
        color="button"
        @click="$emit('close')"
      />
    </div>
  </hit-modal>
</template>

<script>
import {defineComponent} from 'vue';
import {HitModal, HitButton} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

const STATUS_KEYS = {
  send_failed: 'delivery-status-send-failed',
  sent: 'delivery-status-sent',
  in_app: 'delivery-status-in-app',
  received: 'delivery-status-received',
  read: 'delivery-status-read',
  deleted_after_read: 'delivery-status-deleted-after-read',
  not_in_app_missing: 'delivery-status-not-in-app-missing',
  not_in_app_user_missing: 'delivery-status-not-in-app-user-missing',
  not_tracked: 'delivery-status-not-tracked',
};

const EMAIL_STATUS_KEYS = {
  sent: 'email-status-sent',
  failed: 'email-status-failed',
  not_requested: 'email-status-not-requested',
  no_email: 'email-status-no-address',
  skipped_duplicate: 'email-status-skipped-duplicate',
};

export default defineComponent({
  name: 'NotificationBroadcastDeliveriesModal',
  components: {HitModal, HitButton},
  props: {
    open: {type: Boolean, default: false},
    broadcastId: {type: String, default: null},
    broadcastTitle: {type: String, default: ''},
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      tracked: true,
    };
  },
  computed: {
    modalTitle() {
      const title = (this.broadcastTitle || '').trim();
      if (title) {
        return `${this.$t(
          'hit-admin.notifications.deliveries-modal-title'
        )} — ${title}`;
      }
      return this.$t('hit-admin.notifications.deliveries-modal-title');
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.loadDeliveries();
      } else {
        this.rows = [];
        this.error = null;
        this.tracked = true;
      }
    },
  },
  methods: {
    statusLabel(status) {
      const key = STATUS_KEYS[status] || 'delivery-status-sent';
      return this.$t(`hit-admin.notifications.${key}`);
    },
    emailStatusLabel(emailStatus) {
      const key =
        EMAIL_STATUS_KEYS[emailStatus] || 'email-status-not-requested';
      return this.$t(`hit-admin.notifications.${key}`);
    },
    async loadDeliveries() {
      if (!this.broadcastId) return;
      this.loading = true;
      this.error = null;
      try {
        const res = await adminApi.getNotificationBroadcastDeliveries(
          this.broadcastId
        );
        this.rows = res?.rows || [];
        this.tracked = res?.tracked !== false;
      } catch (e) {
        this.error = e?.message || String(e);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.table-wrap {
  max-height: min(60vh, 28rem);
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.5rem;
}

.deliveries-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    padding: 0.45rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }
  th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    font-weight: 600;
    color: #475569;
    z-index: 1;
  }
}

.info-banner {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(59, 130, 246, 0.08);
  color: #1d4ed8;
  font-size: 0.875rem;
}

.status-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  white-space: nowrap;
  &.read {
    background: rgba(0, 153, 124, 0.15);
    color: #03695a;
  }
  &.received {
    background: rgba(59, 130, 246, 0.15);
    color: #1d4ed8;
  }
  &.in_app,
  &.sent {
    background: rgba(100, 116, 139, 0.15);
    color: #475569;
  }
  &.not_in_app,
  &.not_in_app_missing,
  &.not_in_app_user_missing {
    background: rgba(148, 163, 184, 0.2);
    color: #64748b;
  }
  &.deleted_after_read {
    background: rgba(255, 107, 0, 0.12);
    color: #b45309;
  }
  &.send_failed {
    background: rgba(185, 28, 28, 0.12);
    color: #b91c1c;
  }
  &.not_tracked {
    background: rgba(255, 107, 0, 0.1);
    color: #b45309;
  }
  &.email-status.sent {
    background: rgba(0, 153, 124, 0.15);
    color: #03695a;
  }
  &.email-status.failed {
    background: rgba(185, 28, 28, 0.12);
    color: #b91c1c;
  }
  &.not_requested,
  &.no_email,
  &.skipped_duplicate {
    background: rgba(148, 163, 184, 0.2);
    color: #64748b;
  }
}
</style>
